import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import PermissionDeniedAlert from "../../components/common/PermissionDeniedAlert";
import AlertException from "../../components/common/AlertException";

function PerfilDashboardConfigPage() {
  const { id: perfilId } = useParams();
  const navigate = useNavigate();
  const { loading: authLoading, hasPermission } = useAuth();
  const canManage = hasPermission("config.perfis");

  const [perfil, setPerfil] = useState(null);
  const [form, setForm] = useState({
    grupos_iniciam_fechados: false,
    adolescentes_iniciam_fechados: false,
  });
  const [source, setSource] = useState("fallback");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;

    if (!canManage) {
      setLoading(false);
      setError({ kind: "permission" });
      return;
    }

    let mounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [perfilResponse, settingResponse] = await Promise.all([
          api.get(`/perfis/${perfilId}`),
          api.get(`/perfis/${perfilId}/dashboard-settings`),
        ]);

        if (!mounted) return;

        const perfilData = perfilResponse?.data?.data ?? perfilResponse?.data;
        const settingData = settingResponse?.data?.data ?? null;

        setPerfil(perfilData);
        setForm({
          grupos_iniciam_fechados: !!settingData?.grupos_iniciam_fechados,
          adolescentes_iniciam_fechados: !!settingData?.adolescentes_iniciam_fechados,
        });
        setSource(settingData?.source || "fallback");
      } catch (err) {
        if (!mounted) return;

        if (err?.response?.status === 403) {
          setError({ kind: "permission" });
        } else {
          setError(err);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [authLoading, canManage, perfilId]);

  const handleToggle = (field) => {
    setForm((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      await api.put(`/perfis/${perfilId}/dashboard-settings`, {
        grupos_iniciam_fechados: form.grupos_iniciam_fechados,
        adolescentes_iniciam_fechados: form.adolescentes_iniciam_fechados,
      });

      navigate("/painel/perfis", {
        state: { toast: "Configuração de dashboard salva com sucesso." },
      });
    } catch (err) {
      if (err?.response?.status === 403) {
        setError({ kind: "permission" });
      } else {
        setError(err);
      }
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return <LoadingSpinner text="Carregando configuração de dashboard..." />;
  }

  if (error?.kind === "permission") {
    return (
      <PermissionDeniedAlert
        requiredPermission="config.perfis"
        action="configurar visualização do dashboard por perfil"
      />
    );
  }

  if (error) {
    return <AlertException error={error} fallback="Não foi possível carregar a configuração do dashboard." />;
  }

  return (
    <div className="perfis-page perfil-dashboard-config-page">
      <div className="content-header">
        <h1 className="content-title">Configuração do Painel</h1>
        <p className="content-subtitle perfil-dashboard-config-subtitle">
          Perfil: <strong>{perfil?.nome || "—"}</strong>
        </p>
      </div>

      <div className="card perfil-dashboard-config-card">
        <div className="card-body">
          <div className="alert alert-info mb-4" role="alert">
            Origem ativa: <strong>{source === "config" ? "configuração administrativa" : "fallback por regra de negócio"}</strong>.
          </div>

          <div className="d-flex flex-column gap-3">
            <label className="form-check form-switch dashboard-config-switch">
              <input
                className="form-check-input"
                type="checkbox"
                checked={form.grupos_iniciam_fechados}
                onChange={() => handleToggle("grupos_iniciam_fechados")}
              />
              <span className="form-check-label">
                Grupos por meio de entrada iniciam fechados
              </span>
            </label>

            <label className="form-check form-switch dashboard-config-switch">
              <input
                className="form-check-input"
                type="checkbox"
                checked={form.adolescentes_iniciam_fechados}
                onChange={() => handleToggle("adolescentes_iniciam_fechados")}
              />
              <span className="form-check-label">
                Adolescentes iniciam fechados
              </span>
            </label>

            <div className="text-secondary small mt-2">
              Preparado para evolução futura: ocultação total por meio de entrada (não aplicada nesta versão).
            </div>
          </div>
        </div>

        <div className="card-footer d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate("/painel/perfis")}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PerfilDashboardConfigPage;
